/**
 * Cyannect — Terms of Service (public page)
 *
 * DRAFT · legal review required. This file was generated as a starting
 * point for counsel. Numbers and product-specific mechanics are pulled
 * from the live Cyannect codebase (Starter/Growth pricing, dual
 * redemption, cy_ref attribution, cashback ledger, 15% annual add-on
 * discount, per-storefront province entitlements) so a real lawyer can
 * validate them against the actual product.
 */
import Seo from "@/components/Seo";
import { AlertTriangle } from "lucide-react";
import { shortDate, usePricing } from "@/lib/pricing";
import { FinalPlanTerms } from "@/pages/termsPricing";

const LAST_UPDATED = "February 2026";

export default function Terms() {
  const pricing = usePricing();
  const final = !!pricing?.final_active;
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white">
      <Seo
        title="Terms of Service"
        description="Cyannect Terms of Service — Canadian performance-commerce marketplace connecting shoppers with merchant coupons and cashback."
        path="/terms"
      />
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="terms-page">
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 mb-8" data-testid="terms-draft-banner">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Draft — legal review required.</strong> This document is a
            working template generated from the Cyannect product. Send to
            counsel for jurisdiction-specific review (Canada) before public
            launch or first paid signup.
          </div>
        </div>

        <h1 className="font-display text-4xl font-medium text-slate-900">Terms of Service</h1>
        <p className="text-sm text-slate-500 mt-1">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-slate max-w-none mt-8 space-y-6 text-slate-700 leading-relaxed">
          <p>
            These Terms of Service (the “<strong>Terms</strong>”) form a binding
            agreement between you and <strong>Cyannect Inc.</strong>
            (“<strong>Cyannect</strong>”, “<strong>we</strong>”, “<strong>us</strong>”, “<strong>our</strong>”)
            governing your access to and use of cyannect.com, app.cyannect.com,
            our mobile applications and any related products or services
            (collectively the “<strong>Services</strong>”). By creating an
            account or otherwise using the Services you agree to these Terms.
            If you do not agree, do not use the Services.
          </p>

          <h2 id="who">1. Who these Terms apply to</h2>
          <p>
            Cyannect operates a performance-commerce marketplace with two
            classes of users: <strong>Shoppers</strong> (individuals who
            discover and redeem coupons/cashback offers) and
            <strong> Merchants</strong> (businesses that list storefronts,
            campaigns and offers on Cyannect). Some sections apply only to
            Merchants; those sections are prefixed “Merchant”.
          </p>

          <h2 id="accounts">2. Accounts</h2>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>You must be at least the age of majority in your province or territory of residence to create an account.</li>
            <li>You are responsible for maintaining the confidentiality of your credentials and for all activity that occurs under your account.</li>
            <li>You will provide accurate, current and complete information at registration and keep it updated.</li>
            <li>We may suspend or terminate accounts that violate these Terms or that we reasonably suspect of fraudulent behaviour.</li>
          </ul>

          <h2 id="marketplace">3. The Cyannect marketplace</h2>
          <p>
            Cyannect surfaces Merchant offers to Shoppers. When a Shopper
            activates an offer, Cyannect issues an attribution token
            (“<strong>cy_ref</strong>”) that travels with the Shopper to the
            Merchant’s website via the system browser or through a coupon code
            copied by the Shopper. Offers are redeemed in one of two ways:
          </p>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li><strong>Automatic Discount</strong> — the discount applies without a code, based on the cy_ref token.</li>
            <li><strong>Coupon Code</strong> — the Shopper copies a code and applies it at checkout on the Merchant’s site.</li>
          </ul>
          <p>
            <strong>Cyannect does not process the Shopper’s purchase.</strong>{" "}
            The Merchant’s own commerce/payment system remains the seller of
            record and is responsible for order fulfilment, refunds, chargebacks,
            product-liability and consumer-protection compliance (including
            sales tax collection, remittance and receipts).
          </p>

          <h2 id="cashback">4. Cashback rewards (Shoppers)</h2>
          <p>
            Cashback is a discretionary loyalty benefit funded by the platform
            performance-fee split. Cashback accrues to a Shopper wallet when
            Cyannect independently verifies the underlying sale via the
            Merchant’s attribution integration. Verified cashback is subject
            to a business-day holding window (currently 15 business days) to
            allow for returns, chargebacks and fraud review before it becomes
            withdrawable. Cyannect may reverse, adjust or void cashback that
            corresponds to a reversed, refunded or fraudulent sale.
          </p>

          <h2 id="merchant-plans">5. Merchant plans and fees</h2>
          {final ? <FinalPlanTerms pricing={pricing} asOf={shortDate(pricing.effective_at)} /> : <>
          <p>
            Merchants subscribe to a Starter or Growth plan billed monthly or
            annually in Canadian dollars, plus a performance fee applied to
            each verified sale attributed to Cyannect. As of {LAST_UPDATED}:
          </p>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li><strong>Starter</strong> — CAD $39.99/month · includes 1 storefront and 1 province of coverage · verified-sale fee 4.25% or $1.99 (whichever is higher).</li>
            <li><strong>Growth</strong> — CAD $79.99/month · includes 3 storefronts and up to 3 provinces per storefront · verified-sale fee 2.85% or $1.49 (whichever is higher).</li>
            <li><strong>Add-on province</strong> — CAD $11.99/month per additional province, per storefront.</li>
            <li><strong>Add-on storefront</strong> — CAD $19.99/month per additional storefront.</li>
            <li><strong>Annual add-ons</strong> — priced as monthly × 12 × 0.85 (a flat 15% discount off the yearly value), billed annually and aligned to your base-plan renewal.</li>
          </ul>
          </>}
          <p>
            All fees are non-refundable except where required by law. Add-ons
            purchased mid-cycle are prorated in accordance with our Stripe
            billing configuration. Cyannect reserves the right to adjust
            pricing with notice; changes apply at the start of the next
            billing period.
          </p>

          <h2 id="conversion-finality">6. Verified-sale finality (Merchants)</h2>
          <p>
            A sale reported to Cyannect enters a <strong>finality window</strong>
            (currently 14 business days). During this window the sale can be
            reversed if the Merchant reports a refund, chargeback or fraud
            adjustment through the conversions API. After the window closes,
            the performance fee is settled and cashback becomes payable to the
            Shopper. Cyannect may extend the window during fraud investigations.
          </p>

          <h2 id="merchant-conduct">7. Merchant conduct and content</h2>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>Merchants must not create a duplicate storefront for the same brand solely to avoid province add-on charges. Duplicate-brand storefronts on the same account are automatically blocked.</li>
            <li>Merchants are solely responsible for the accuracy of offer terms, product listings, discount amounts and any restrictions (min-cart, expiry, product limits) they publish on Cyannect.</li>
            <li>Merchants grant Cyannect a limited, worldwide, royalty-free licence to reproduce, adapt and publicly display the storefront name, logo, banner, product images and offer copy solely to operate and promote the Services.</li>
            <li>Prohibited content includes anything unlawful, misleading, infringing, defamatory, or that targets sanctioned regions or industries excluded by our acceptable-use policy.</li>
          </ul>

          <h2 id="shopper-conduct">8. Shopper conduct</h2>
          <p>
            Shoppers must not: (a) fabricate transactions or manipulate
            attribution; (b) share, sell or scrape coupon codes; (c) create
            multiple accounts to abuse per-customer offer limits; or (d) use
            automation to interact with the marketplace. Suspected abuse may
            result in reversal of cashback and account suspension.
          </p>

          <h2 id="ip">9. Intellectual property</h2>
          <p>
            The Services, including all software, design, text, graphics and
            trademarks (Cyannect, cy_ref, and related marks), are owned by
            Cyannect or its licensors and are protected by Canadian and
            international intellectual-property laws. Except for the limited
            licences granted in these Terms, no rights are transferred to you.
          </p>

          <h2 id="privacy">10. Privacy</h2>
          <p>
            Our <a href="/privacy" className="underline text-cyan-700">Privacy Policy</a>{" "}
            explains what personal information we collect, how we use it and
            the choices you have. By using the Services you consent to that
            processing.
          </p>

          <h2 id="warranties">11. Disclaimers</h2>
          <p>
            THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE”. TO THE
            MAXIMUM EXTENT PERMITTED BY LAW, CYANNECT DISCLAIMS ALL WARRANTIES,
            EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY,
            FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. CYANNECT
            DOES NOT WARRANT THAT ANY MERCHANT WILL HONOUR AN OFFER, THAT AN
            ATTRIBUTED SALE WILL RESULT IN CASHBACK, OR THAT THE SERVICES WILL
            BE UNINTERRUPTED OR ERROR-FREE.
          </p>

          <h2 id="liability">12. Limitation of liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, CYANNECT’S TOTAL LIABILITY
            ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICES IS
            LIMITED, IN THE AGGREGATE, TO THE GREATER OF: (a) THE AMOUNTS
            PAID BY YOU TO CYANNECT IN THE SIX MONTHS PRECEDING THE CLAIM, OR
            (b) CAD $100. IN NO EVENT WILL CYANNECT BE LIABLE FOR INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, LOST
            PROFITS OR LOSS OF DATA, WHETHER IN CONTRACT, TORT OR OTHERWISE.
          </p>

          <h2 id="indemnity">13. Indemnification</h2>
          <p>
            You will indemnify and hold harmless Cyannect and its affiliates,
            officers, employees and agents from and against any claims,
            damages, liabilities, costs and expenses (including reasonable
            legal fees) arising out of: (a) your breach of these Terms; (b)
            your content, products or offers; or (c) your violation of any
            applicable law or third-party right.
          </p>

          <h2 id="termination">14. Termination</h2>
          <p>
            You may cancel your account at any time from the settings screen.
            Merchant subscriptions cancel at the end of the current billing
            period. We may suspend or terminate your account immediately if
            you breach these Terms, if we detect fraud, or if required by law.
            Sections that by their nature should survive termination will
            survive, including intellectual-property, disclaimers, limitation
            of liability, indemnity and governing law.
          </p>

          <h2 id="law">15. Governing law and dispute resolution</h2>
          <p>
            These Terms are governed by the laws of the Province of Ontario
            and the federal laws of Canada applicable therein, without regard
            to conflict-of-laws principles. Any dispute will be resolved by
            the courts located in Toronto, Ontario, and you consent to the
            jurisdiction and venue of those courts, except where applicable
            consumer-protection law grants you a right to sue elsewhere.
          </p>

          <h2 id="changes">16. Changes to these Terms</h2>
          <p>
            We may update these Terms from time to time. If the update is
            material, we will provide notice through the Services or by email
            before it takes effect. Continued use after the effective date
            constitutes acceptance.
          </p>

          <h2 id="contact">17. Contact</h2>
          <p>
            Questions? Email us at{" "}
            <a href="mailto:support@cyannect.com" className="underline text-cyan-700">support@cyannect.com</a>{" "}
            or write to Cyannect Inc., Ontario, Canada.
          </p>
        </div>
      </div>
    </div>
  );
}
